import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Shield, ChevronDown, Settings, Crown, Users as UsersIcon, Key, RefreshCw, AlertTriangle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { notify, fmtError } from "@/lib/notify";

export default function UserMenu() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState(null);
  const [showPwModal, setShowPwModal] = useState(false);
  const [showRegenModal, setShowRegenModal] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    api.get("/workspace/me").then((r) => setMe(r.data)).catch(() => setMe(null));
    const reload = () => api.get("/workspace/me").then((r) => setMe(r.data)).catch(() => {});
    window.addEventListener("noam:org-updated", reload);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("noam:org-updated", reload);
    };
  }, []);

  if (!user || user === false || user === null) return null;

  const initials = (user.email || "?").slice(0, 2).toUpperCase();
  const orgRole = me?.user?.role;
  const isUltraAdmin = !!me?.user?.is_ultra_admin;
  const canManageUsers = orgRole === "admin" || orgRole === "super_admin" || isUltraAdmin;
  const canRegenCarto = orgRole === "admin" || orgRole === "super_admin" || isUltraAdmin;
  const orgSlug = me?.organisation?.slug || "";
  const orgName = me?.organisation?.name || user.organisation || "";

  const onLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="relative" ref={ref} data-testid="user-menu">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-md hover:bg-white/5 transition-colors"
        data-testid="user-menu-btn"
      >
        <div className="w-7 h-7 rounded-md bg-[#378ADD]/20 border border-[#378ADD]/40 flex items-center justify-center text-[10px] font-semibold text-[#378ADD]">
          {initials}
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 noam-card py-1.5 z-50" data-testid="user-menu-dropdown">
          <div className="px-3 py-2 border-b border-white/5">
            <div className="text-sm text-white truncate">{user.name || user.email}</div>
            <div className="text-[11px] text-zinc-500 truncate">{user.email}</div>
            {user.organisation && (
              <div className="text-[11px] text-zinc-500 truncate mt-0.5">{user.organisation}</div>
            )}
            <div className="mt-1.5 inline-flex text-[10px] px-1.5 py-0.5 rounded border border-[#378ADD]/30 bg-[#378ADD]/10 text-[#B5D4F4]">
              {user.role}
            </div>
          </div>
          {isUltraAdmin && (
            <Link
              to="/ultra-admin"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#EF9F27] hover:bg-[#EF9F27]/10"
              data-testid="user-menu-ultra-admin"
            >
              <Crown className="w-3.5 h-3.5" /> Ultra Admin
            </Link>
          )}
          {canManageUsers && (
            <Link
              to="/app/users"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-white/5 hover:text-white"
              data-testid="user-menu-users"
            >
              <UsersIcon className="w-3.5 h-3.5" /> Utilisateurs
            </Link>
          )}
          {user.role === "admin" && (
            <Link
              to="/app/admin"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-white/5 hover:text-white"
              data-testid="user-menu-admin"
            >
              <Shield className="w-3.5 h-3.5" /> Administration
            </Link>
          )}
          <Link
            to="/app/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-white/5 hover:text-white"
            data-testid="user-menu-settings"
          >
            <Settings className="w-3.5 h-3.5" /> {t("settings.menu_label")}
          </Link>
          <button
            onClick={() => { setShowPwModal(true); setOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-white/5 hover:text-white"
            data-testid="user-menu-change-password"
          >
            <Key className="w-3.5 h-3.5" /> Changer mon mot de passe
          </button>
          {canRegenCarto && (
            <button
              onClick={() => { setShowRegenModal(true); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-white/5 hover:text-[#EF9F27]"
              data-testid="user-menu-regen-cartography"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Régénérer toute la cartographie
            </button>
          )}
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-white/5 hover:text-[#E24B4A]"
            data-testid="user-menu-logout"
          >
            <LogOut className="w-3.5 h-3.5" /> Se déconnecter
          </button>
        </div>
      )}
      {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}
      {showRegenModal && (
        <RegenerateCartographyModal
          orgSlug={orgSlug}
          orgName={orgName}
          onClose={() => setShowRegenModal(false)}
        />
      )}
    </div>
  );
}

// V23.16.6 — Self-service password change modal
function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const strength = (() => {
    let s = 0;
    if (next.length >= 8) s++;
    if (next.length >= 12) s++;
    if (/[a-z]/.test(next) && /[A-Z]/.test(next)) s++;
    if (/\d/.test(next)) s++;
    if (/[^A-Za-z0-9]/.test(next)) s++;
    return s; // 0-5
  })();
  const strengthColor = ["#52525B", "#E24B4A", "#EF9F27", "#EF9F27", "#1D9E75", "#1D9E75"][strength];
  const strengthLabel = ["—", "Très faible", "Faible", "Correct", "Bon", "Excellent"][strength];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) {
      setError("Le nouveau mot de passe doit faire au moins 8 caractères.");
      return;
    }
    if (next !== confirm) {
      setError("La confirmation ne correspond pas.");
      return;
    }
    if (current === next) {
      setError("Le nouveau mot de passe doit être différent de l'actuel.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/password/change", { current_password: current, new_password: next });
      notify.success("Mot de passe mis à jour.");
      onClose();
    } catch (err) {
      setError(fmtError(err, "Impossible de changer le mot de passe."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="change-password-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="noam-card p-5 w-full max-w-sm space-y-3"
      >
        <div className="flex items-center gap-2 mb-1">
          <Key className="w-4 h-4 text-[#7F77DD]" />
          <h3 className="text-sm font-semibold text-zinc-100">Changer mon mot de passe</h3>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Mot de passe actuel</label>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="noam-input w-full mt-1"
            required
            autoComplete="current-password"
            data-testid="change-password-current"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Nouveau mot de passe</label>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="noam-input w-full mt-1"
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="change-password-new"
          />
          {next && (
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1 rounded bg-white/5 overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{ width: `${(strength / 5) * 100}%`, background: strengthColor }}
                />
              </div>
              <span className="text-[10px]" style={{ color: strengthColor }}>{strengthLabel}</span>
            </div>
          )}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Confirmer</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="noam-input w-full mt-1"
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="change-password-confirm"
          />
        </div>
        {error && <div className="text-xs text-[#FCA5A5]" data-testid="change-password-error">{error}</div>}
        <div className="text-[10px] text-zinc-500 leading-snug">
          Min 8 caractères · 1 lettre + 1 chiffre. Vous pouvez le changer autant de fois que vous voulez.
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded border border-white/10 text-zinc-400 hover:text-zinc-100"
            data-testid="change-password-cancel"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded bg-[#7F77DD] text-white disabled:opacity-40"
            data-testid="change-password-submit"
          >
            {busy ? "Enregistrement…" : "Mettre à jour"}
          </button>
        </div>
      </form>
    </div>
  );
}


// Régénérer toute la cartographie — purge graph + river + caches IA puis
// laisse les modules régénérer au prochain affichage avec le contexte actuel.
function RegenerateCartographyModal({ orgSlug, orgName, onClose }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (confirm !== orgSlug) {
      setError(`Tape exactement le slug de l'organisation : ${orgSlug}`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/workspace/admin/regenerate-cartography", { confirm_slug: orgSlug });
      setResult(r.data);
      notify.success(`Cartographies purgées : ${r.data.total_deleted} artefact(s) effacé(s).`);
    } catch (err) {
      setError(fmtError(err, "Impossible de régénérer."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="regen-cartography-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="noam-card p-5 w-full max-w-md space-y-3"
      >
        {!result ? (
          <form onSubmit={submit} className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <RefreshCw className="w-4 h-4 text-[#EF9F27]" />
              <h3 className="text-sm font-semibold text-zinc-100">Régénérer toute la cartographie</h3>
            </div>
            <div className="text-[12px] text-zinc-300 leading-relaxed">
              Cette action efface <strong className="text-white">tous les artefacts générés par l&apos;IA</strong> sur l&apos;organisation
              {" "}<strong className="text-white">{orgName || orgSlug}</strong> :
            </div>
            <ul className="text-[11px] text-zinc-400 space-y-0.5 pl-4 list-disc">
              <li>Graphe Doctrine 51 (nœuds, arêtes, analyses NOAM)</li>
              <li>Value Chain River (chaîne de valeur + scénarios)</li>
              <li>Fractale Origination, Capital cascade, Gravité opérationnelle</li>
              <li>Chaînage causal, Asymétries dynamiques, ADN organisationnel</li>
              <li>Scans Phantom Detector, historique de scoring</li>
            </ul>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-[11px] text-amber-200/90 leading-snug">
                <strong>Tes données saisies sont préservées</strong> (positionnement, annuaire, risques, RH, audits, fournisseurs…). Seules les cartographies IA sont régénérées au prochain affichage à partir du contexte actuel.
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">
                Pour confirmer, tape le slug : <span className="text-[#EF9F27]">{orgSlug}</span>
              </label>
              <input
                type="text"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={orgSlug}
                className="noam-input w-full mt-1 font-mono text-sm"
                autoFocus
                data-testid="regen-cartography-confirm"
              />
            </div>
            {error && <div className="text-xs text-[#FCA5A5]" data-testid="regen-cartography-error">{error}</div>}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
              <button
                type="button"
                onClick={onClose}
                className="text-xs px-3 py-1.5 rounded border border-white/10 text-zinc-400 hover:text-zinc-100"
                data-testid="regen-cartography-cancel"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={busy || confirm !== orgSlug}
                className="text-xs px-3 py-1.5 rounded bg-[#EF9F27] text-zinc-900 font-semibold disabled:opacity-40 inline-flex items-center gap-1"
                data-testid="regen-cartography-submit"
              >
                {busy ? "Purge en cours…" : <><RefreshCw className="w-3 h-3" /> Régénérer</>}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-3" data-testid="regen-cartography-result">
            <div className="flex items-center gap-2 mb-1">
              <RefreshCw className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-semibold text-zinc-100">Cartographies régénérées</h3>
            </div>
            <div className="text-[12px] text-zinc-300">
              <strong className="text-white">{result.total_deleted}</strong> artefact(s) purgé(s).
              {" "}Ouvre maintenant un module (Doctrine 51, Value Chain River, Asymétries…) pour relancer la génération avec ton contexte actuel.
            </div>
            {Object.keys(result.details || {}).length > 0 && (
              <details className="text-[11px] text-zinc-500">
                <summary className="cursor-pointer hover:text-zinc-300">Détails par collection</summary>
                <ul className="mt-1.5 space-y-0.5 pl-3 font-mono text-[10px]">
                  {Object.entries(result.details).map(([k, v]) => (
                    <li key={k}><span className="text-zinc-400">{k}</span> : {v}</li>
                  ))}
                </ul>
              </details>
            )}
            <div className="flex items-center justify-end pt-2 border-t border-white/5">
              <button
                onClick={onClose}
                className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white"
                data-testid="regen-cartography-close"
              >
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
